import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FlaskConical, Search, Building2, ExternalLink, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '@/components/Pagination';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { normalizeStageLabel } from '@/utils/stage';

const API = '/api';

const STAGES = ['Pre-Clinical', 'IND', 'Phase 1', 'Phase 2', 'Phase 3', 'Clinical Stage', 'Commercial'];
const STATUSES = ['Pipeline', 'Active', 'In Negotiation', 'On Hold', 'Closed', 'Dropped'];

const STAGE_COLORS = {
  'Discovery': 'bg-zinc-100 text-zinc-700',
  'Pre-Clinical': 'bg-slate-100 text-slate-700',
  'IND': 'bg-sky-100 text-sky-700',
  'Phase 1': 'bg-blue-100 text-blue-700',
  'Phase 2': 'bg-violet-100 text-violet-700',
  'Phase 3': 'bg-amber-100 text-amber-700',
  'Clinical Stage': 'bg-fuchsia-100 text-fuchsia-700',
  'Commercial': 'bg-green-100 text-green-700',
  'Platform': 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS = {
  'Pipeline': 'bg-slate-100 text-slate-600',
  'Active': 'bg-sky-100 text-sky-700',
  'In Negotiation': 'bg-purple-100 text-purple-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  'Closed': 'bg-green-100 text-green-700',
  'Dropped': 'bg-rose-100 text-rose-700',
};

export default function AssetsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  // Deep-link support: /assets?search=HB0056 (from global search) must
  // prefill the search box AND flip the source tab to "All" — otherwise
  // the user lands on the default 161-row "Active" subset and the asset
  // they clicked appears nowhere (2026-06 user report: "clicking asset
  // in global search doesn't take me there").
  const _initialSearch = searchParams.get('search') || '';
  const [search, setSearch] = useState(_initialSearch);
  const [searchInput, setSearchInput] = useState(_initialSearch);
  const [sourceFilter, setSourceFilter] = useState(_initialSearch ? 'all' : 'manual');
  // Multi-select filters — arrays of selected values. URL param can be
  // either single ("Phase 2") or comma-list ("Phase 2,Phase 3"). We split
  // defensively so deep-link URLs from the old single-select era still work.
  const _initialFromUrl = (k) => {
    const raw = searchParams.get(k) || '';
    return raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
  };
  const [stageFilter, setStageFilter] = useState(_initialFromUrl('stage'));
  const [statusFilter, setStatusFilter] = useState(_initialFromUrl('status'));
  const [orphanFilter, setOrphanFilter] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);
  const [reverifying, setReverifying] = useState(false);
  const [rareStatus, setRareStatus] = useState(null);
  useEffect(() => {
    if (!orphanFilter) return;
    axios.get(`${API}/assets/classify-rare-orphan/status`, { withCredentials: true }).then(r => setRareStatus(r.data)).catch(() => {});
  }, [orphanFilter]);
  const reverifyRareOrphan = async () => {
    if (!window.confirm(`Re-run the AI rare/orphan check on all ${total.toLocaleString()} assets? It runs in the background (about ${Math.max(1, Math.round(total / 20 / 10))}–${Math.max(2, Math.round(total / 20 / 4))} min) and uses your AI key balance.`)) return;
    setReverifying(true);
    try {
      const r = await axios.post(`${API}/assets/classify-rare-orphan?force=true`, {}, { withCredentials: true });
      toast.success(`Re-checking ${r.data.queued} assets with AI — badges update as it runs; reload in a few minutes.`, { duration: 10000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the re-check');
    } finally {
      setReverifying(false);
    }
  };
  const [countryFilter, setCountryFilter] = useState(_initialFromUrl('country'));
  const [countryOptions, setCountryOptions] = useState([]);
  // Feb 2026 v8 · Therapeutic Area filter (QA / validation tool).
  // `taMode` controls include-vs-exclude semantics — flipped inline
  // via the toggle button that renders beside the multi-select.
  const [taFilter, setTaFilter] = useState(_initialFromUrl('ta'));
  const [taMode, setTaMode] = useState(searchParams.get('ta_mode') === 'exclude' ? 'exclude' : 'include');
  const [taOptions, setTaOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sourceCounts, setSourceCounts] = useState({ manual: 0, database: 0, all: 0 });
  // Asset category filter (top sub-tab bar). Read once on mount, refreshed
  // by useLocation polling below.
  const _initialCat = (() => {
    try { return new URLSearchParams(window.location.search).get('category') || ''; }
    catch { return ''; }
  })();
  const [categoryFilter, setCategoryFilter] = useState(_initialCat);
  const searchTimeoutRef = useRef(null);

  const fetchAssets = async (page = 1, searchQuery = '', sourceVal = sourceFilter, stageVal = stageFilter, statusVal = statusFilter, orphanVal = orphanFilter, countryVal = countryFilter, catVal = categoryFilter, taVal = taFilter, taModeVal = taMode) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 5000 });
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      if (sourceVal && sourceVal !== 'all') params.set('source', sourceVal);
      // Multi-select arrays are sent as comma-separated strings; backend
      // splits them back. Single-element arrays are equivalent to the legacy
      // single-value form, so this is fully backwards-compatible.
      const _csv = (v) => Array.isArray(v) ? v.filter(Boolean).join(',') : (v || '');
      const stageCsv = _csv(stageVal);
      const statusCsv = _csv(statusVal);
      const countryCsv = _csv(countryVal);
      const taCsv = _csv(taVal);
      if (stageCsv) params.set('stage', stageCsv);
      if (statusCsv) params.set('status', statusCsv);
      if (orphanVal) params.set('orphan_rare', 'true');
      if (countryCsv) params.set('country', countryCsv);
      if (catVal) params.set('category', catVal);
      if (taCsv) {
        params.set('ta', taCsv);
        params.set('ta_mode', taModeVal || 'include');
      }
      const res = await axios.get(`${API}/assets?${params}`, { withCredentials: true });
      const assetsData = res.data;
      setAssets(assetsData.items || assetsData || []);
      setTotalPages(assetsData.pages || 1);
      setTotalCount(assetsData.total || 0);
      setCurrentPage(assetsData.page || 1);
      if (assetsData.source_counts) setSourceCounts(assetsData.source_counts);
      if (assetsData.orphan_count !== undefined) setOrphanCount(assetsData.orphan_count);
    } catch {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API}/assets/countries`, { withCredentials: true });
      setCountryOptions(res.data.items || []);
    } catch (err) {
      // Silent retry once after a short delay — handles a race where the
      // auth cookie isn't ready on the first render after login.
      try {
        await new Promise(r => setTimeout(r, 800));
        const res2 = await axios.get(`${API}/assets/countries`, { withCredentials: true });
        setCountryOptions(res2.data.items || []);
      } catch {/* non-blocking */}
    }
  };

  // Feb 2026 v8 · Reload TA options whenever the country / stage /
  // source / orphan filters change so the counts stay honest ("183
  // Cardiovascular assets in China Phase 3+" recomputes when the user
  // clicks another country).
  const fetchTAs = async (stageVal = stageFilter, countryVal = countryFilter, orphanVal = orphanFilter, sourceVal = sourceFilter, searchQuery = search) => {
    try {
      const params = new URLSearchParams();
      const csv = (v) => Array.isArray(v) ? v.filter(Boolean).join(',') : (v || '');
      if (csv(stageVal)) params.set('stage', csv(stageVal));
      if (csv(countryVal)) params.set('country', csv(countryVal));
      if (orphanVal) params.set('orphan_rare', 'true');
      if (sourceVal && sourceVal !== 'all') params.set('source', sourceVal);
      if (searchQuery && searchQuery.trim()) params.set('search', searchQuery.trim());
      const res = await axios.get(`${API}/assets/therapeutic-areas?${params}`, { withCredentials: true });
      setTaOptions(res.data.items || []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { fetchAssets(1, _initialSearch, _initialSearch ? 'all' : sourceFilter, stageFilter, statusFilter, orphanFilter, countryFilter, categoryFilter, taFilter, taMode); fetchCountries(); fetchTAs(); }, []);

  // Sync the category filter with the URL whenever the SubTabBar navigates
  // between Pipeline / Formulation / Academic / DTx / Off-Patent.
  const location = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const nextCat = sp.get('category') || '';
    if (nextCat !== categoryFilter) {
      setCategoryFilter(nextCat);
      fetchAssets(1, search, sourceFilter, stageFilter, statusFilter, orphanFilter, countryFilter, nextCat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setCurrentPage(1);
      fetchAssets(1, value, sourceFilter, stageFilter, statusFilter, orphanFilter, countryFilter);
    }, 400);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchAssets(newPage, search, sourceFilter, stageFilter, statusFilter, orphanFilter, countryFilter);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSourceFilterChange = (newFilter) => {
    setSourceFilter(newFilter);
    setCurrentPage(1);
    fetchAssets(1, search, newFilter, stageFilter, statusFilter, orphanFilter, countryFilter);
  };

  const handleStageFilterChange = (newStages) => {
    // newStages is an array from the multi-select; preserved as-is.
    setStageFilter(newStages);
    setCurrentPage(1);
    fetchAssets(1, search, sourceFilter, newStages, statusFilter, orphanFilter, countryFilter, categoryFilter, taFilter, taMode);
    fetchTAs(newStages, countryFilter, orphanFilter, sourceFilter, search);
  };

  const handleStatusFilterChange = (newStatuses) => {
    setStatusFilter(newStatuses);
    setCurrentPage(1);
    fetchAssets(1, search, sourceFilter, stageFilter, newStatuses, orphanFilter, countryFilter, categoryFilter, taFilter, taMode);
  };

  const handleOrphanFilterChange = () => {
    const newVal = !orphanFilter;
    setOrphanFilter(newVal);
    setCurrentPage(1);
    fetchAssets(1, search, sourceFilter, stageFilter, statusFilter, newVal, countryFilter, categoryFilter, taFilter, taMode);
    fetchTAs(stageFilter, countryFilter, newVal, sourceFilter, search);
  };

  const handleCountryFilterChange = (newCountries) => {
    setCountryFilter(newCountries);
    setCurrentPage(1);
    fetchAssets(1, search, sourceFilter, stageFilter, statusFilter, orphanFilter, newCountries, categoryFilter, taFilter, taMode);
    fetchTAs(stageFilter, newCountries, orphanFilter, sourceFilter, search);
  };

  const handleTaFilterChange = (newTas) => {
    setTaFilter(newTas);
    setCurrentPage(1);
    fetchAssets(1, search, sourceFilter, stageFilter, statusFilter, orphanFilter, countryFilter, categoryFilter, newTas, taMode);
  };

  const handleTaModeToggle = () => {
    const next = taMode === 'include' ? 'exclude' : 'include';
    setTaMode(next);
    if (taFilter.length > 0) {
      setCurrentPage(1);
      fetchAssets(1, search, sourceFilter, stageFilter, statusFilter, orphanFilter, countryFilter, categoryFilter, taFilter, next);
    }
  };

  const exportXlsx = async () => {
    // Reuses the live filter shape so the download "snaps" to whatever the
    // user is currently looking at (multi-select stage/status/country
    // included). Triggers the browser's native download via a Blob URL —
    // no server-side session/cookie juggling needed.
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (sourceFilter && sourceFilter !== 'all') params.set('source', sourceFilter);
      const csv = (v) => Array.isArray(v) ? v.filter(Boolean).join(',') : (v || '');
      if (csv(stageFilter)) params.set('stage', csv(stageFilter));
      if (csv(statusFilter)) params.set('status', csv(statusFilter));
      if (orphanFilter) params.set('orphan_rare', 'true');
      if (csv(countryFilter)) params.set('country', csv(countryFilter));
      if (csv(taFilter)) {
        params.set('ta', csv(taFilter));
        params.set('ta_mode', taMode || 'include');
      }
      toast.message(`Preparing export of ${totalCount.toLocaleString()} asset${totalCount === 1 ? '' : 's'}…`);
      const res = await axios.get(`${API}/assets-export.xlsx?${params}`, {
        withCredentials: true,
        responseType: 'blob',
        timeout: 90000,
      });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `foresightrx_assets_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Export failed');
    }
  };

  // Initial-load spinner: only fire on first paint (loading && no assets yet).
  // Subsequent filter-change refetches don't unmount the filter row — that
  // would destroy the MultiSelectFilter's local `open` state mid-selection
  // and make "select multiple" impossible.
  if (loading && assets.length === 0) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Assets
            <span className="ml-3 text-sm font-normal text-slate-500 tabular-nums">· {(sourceCounts.all || 0).toLocaleString()} total</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">Molecules and products from biotech companies</p>
        </div>
        {/* Standardised Export button — top-right of every list page */}
        <button
          onClick={exportXlsx}
          disabled={totalCount === 0}
          className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-md hover:bg-emerald-100 hover:border-emerald-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="assets-export-xlsx"
          title="Download a .xlsx report of the current filter — perfect for pharma proposals"
        >
          <Download className="w-3.5 h-3.5" />
          Export Excel
          {totalCount > 0 && (
            <span className="text-[10px] text-emerald-600">({totalCount.toLocaleString()})</span>
          )}
        </button>
      </div>

      {/* Filters (cleaned 2026-02): inline search, statuses dropdown, and
          Rare/Orphan classify button removed per user — global Ctrl+K search
          and the dashboard handle the first two; Rare/Orphan classification
          now happens automatically as a post-commit step in the asset
          import pipeline (Data Ops → Asset Imports). The Rare/Orphan view
          filter is preserved here. */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex bg-slate-100 rounded-md p-0.5 mr-1" data-testid="assets-source-filter">
          <button type="button" onClick={() => handleSourceFilterChange('manual')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'manual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="assets-filter-my-clients">Active Assets ({sourceCounts.manual})</button>
          <button type="button" onClick={() => handleSourceFilterChange('bio_international')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'bio_international' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="assets-filter-database">Database ({sourceCounts.database})</button>
          <button type="button" onClick={() => handleSourceFilterChange('all')}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded transition-colors ${sourceFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="assets-filter-all">All ({sourceCounts.all})</button>
        </div>
        <MultiSelectFilter
          label="All Stages"
          pluralName="stages"
          options={STAGES}
          value={stageFilter}
          onChange={handleStageFilterChange}
          width="w-40"
          testId="assets-stage-filter"
        />
        <MultiSelectFilter
          label="All Countries"
          pluralName="countries"
          options={countryOptions.map(c => ({
            value: c.name,
            label: c.name,
            hint: `${c.count}${c.company_count ? ` · ${c.company_count} co${c.company_count === 1 ? '' : 's'}` : ''}`,
          }))}
          value={countryFilter}
          onChange={handleCountryFilterChange}
          width="w-44"
          testId="assets-country-filter"
        />
        {/* Therapeutic Area filter — Include mode (default) or Exclude
            mode, toggled via the small chip beside it. Counts update
            dynamically off other filters via /assets/therapeutic-areas. */}
        <div className="flex items-center gap-1">
          <MultiSelectFilter
            label={taMode === 'exclude' ? 'Exclude TAs' : 'All TAs'}
            pluralName="TAs"
            options={taOptions.map(t => ({
              value: t.name,
              // Feb 2026 v9 · render the __BLANK__ sentinel as a
              // distinct "(No TA)" row so users can see and act on
              // the data-quality gap explicitly. Adds a warning
              // emoji-free indicator via bracketed label so it sorts
              // apart from real TA values.
              label: t.name === '__BLANK__' ? '(No TA / blank)' : t.name,
              hint: `${t.count}${t.name === '__BLANK__' ? ' · data gap' : ''}`,
            }))}
            value={taFilter}
            onChange={handleTaFilterChange}
            width="w-48"
            testId="assets-ta-filter"
            searchable
          />
          <button
            type="button"
            onClick={handleTaModeToggle}
            className={`h-9 px-2 text-[10px] font-bold uppercase tracking-wide rounded-md border transition-colors ${
              taMode === 'exclude'
                ? 'bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100'
                : 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
            }`}
            title={taMode === 'exclude'
              ? 'EXCLUDE mode — hides assets in the selected TAs AND assets with blank TA (data-quality safety). Click to switch to INCLUDE mode.'
              : 'INCLUDE mode — shows only assets in the selected TAs. Add "(No TA / blank)" to also include blank-TA rows. Click to switch to EXCLUDE mode.'}
            data-testid="assets-ta-mode-toggle"
          >
            {taMode === 'exclude' ? 'Exclude' : 'Include'}
          </button>
        </div>
        <button
          type="button"
          onClick={handleOrphanFilterChange}
          className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md border transition-colors ${
            orphanFilter
              ? 'bg-rose-50 border-rose-300 text-rose-700'
              : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
          data-testid="assets-filter-orphan-rare"
          title="Toggle the Rare/Orphan filter. Classification now happens automatically during import (Data Ops → Asset Imports)."
        >
          Rare / Orphan ({orphanCount})
        </button>
        {orphanFilter && (
          <button type="button" onClick={reverifyRareOrphan} disabled={reverifying}
            className="px-2.5 py-1.5 text-[11px] font-medium rounded-md border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            data-testid="assets-reverify-rare-orphan"
            title="Re-run the AI rare/orphan check on every asset (FDA/EMA orphan designation, Orphanet/NORD, prevalence <200k US). Hover a badge to see the reason for each asset.">
            {reverifying ? 'Re-checking…' : 'Re-verify with AI'}
          </button>
        )}
        {orphanFilter && rareStatus && (
          <span className="text-[11px] text-slate-500" data-testid="rare-orphan-status">
            AI-verified rare: <b className="text-slate-700">{rareStatus.rare_orphan_count.toLocaleString()}</b> of {rareStatus.classified.toLocaleString()} checked ({rareStatus.rare_share_pct}%)
            {rareStatus.remaining > 0 ? ` · ${rareStatus.remaining.toLocaleString()} not yet checked (shown only if their text says "rare"/"orphan")` : ''}
            {rareStatus.last_classified_at ? ` · last run ${new Date(rareStatus.last_classified_at).toLocaleDateString()}` : ''}
          </span>
        )}
        {((stageFilter && stageFilter.length) || orphanFilter || (countryFilter && countryFilter.length) || (taFilter && taFilter.length)) ? (
          <button onClick={() => { setStageFilter([]); setStatusFilter([]); setSearchInput(''); setSearch(''); setOrphanFilter(false); setCountryFilter([]); setTaFilter([]); setTaMode('include'); fetchAssets(1, '', sourceFilter, [], [], false, [], categoryFilter, [], 'include'); fetchTAs([], [], false, sourceFilter, ''); }} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="clear-filters">Clear</button>
        ) : null}
      </div>

      {/* Feb 2026 v8 · Live filter summary — quick sanity check that
          the current query matches the operator's intent (spec §
          "Nice-to-have"). Renders only when any filter is active so
          it doesn't clutter the default view. */}
      {((stageFilter && stageFilter.length) || (countryFilter && countryFilter.length) || (taFilter && taFilter.length) || orphanFilter) && (
        <div
          className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 flex items-center gap-4 flex-wrap text-[11px]"
          data-testid="assets-filter-summary"
        >
          <div className="flex items-center gap-1.5">
            <span className="uppercase tracking-wide text-[9px] font-bold text-slate-500">Result</span>
            <span className="text-lg font-black text-slate-900 tabular-nums" data-testid="assets-filter-summary-count">{totalCount.toLocaleString()}</span>
            <span className="text-slate-500">assets</span>
          </div>
          {countryFilter && countryFilter.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="uppercase tracking-wide text-[9px] font-bold text-slate-500">Country:</span>
              {countryFilter.slice(0, 3).map(c => (
                <span key={c} className="rounded-full border border-slate-300 bg-white px-1.5 py-0 text-slate-700">{c}</span>
              ))}
              {countryFilter.length > 3 && <span className="text-slate-500">+{countryFilter.length - 3}</span>}
            </div>
          )}
          {stageFilter && stageFilter.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="uppercase tracking-wide text-[9px] font-bold text-slate-500">Stage:</span>
              {stageFilter.slice(0, 4).map(s => (
                <span key={s} className="rounded-full border border-slate-300 bg-white px-1.5 py-0 text-slate-700">{s}</span>
              ))}
              {stageFilter.length > 4 && <span className="text-slate-500">+{stageFilter.length - 4}</span>}
            </div>
          )}
          {taFilter && taFilter.length > 0 && (
            <div className="flex items-center gap-1">
              <span className={`uppercase tracking-wide text-[9px] font-bold ${taMode === 'exclude' ? 'text-rose-700' : 'text-emerald-700'}`}>
                {taMode === 'exclude' ? 'TAs excluded:' : 'TAs included:'}
              </span>
              {taFilter.slice(0, 3).map(t => (
                <span key={t}
                  className={`rounded-full border px-1.5 py-0 ${
                    taMode === 'exclude'
                      ? 'border-rose-300 bg-rose-50 text-rose-700'
                      : 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  }`}>{t === '__BLANK__' ? '(No TA)' : t}</span>
              ))}
              {taFilter.length > 3 && <span className="text-slate-500">+{taFilter.length - 3}</span>}
            </div>
          )}
          {/* Feb 2026 v9 · Data-quality callout. When the current
              country/stage filter would return a large number of
              blank-TA rows AND the user hasn't touched the TA filter,
              flag it so they know the TA counts don't tell the whole
              story. */}
          {(!taFilter || taFilter.length === 0) && taOptions.some(o => o.name === '__BLANK__' && o.count > 5) && (
            <span
              className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0 text-amber-800 font-medium"
              title="These assets don't have a therapeutic_area value set. They will silently be included in Exclude-mode results unless the TA filter is used."
              data-testid="assets-filter-blank-ta-callout"
            >
              ⚠ {taOptions.find(o => o.name === '__BLANK__')?.count.toLocaleString()} with blank TA
            </span>
          )}
          {orphanFilter && (
            <span className="rounded-full border border-rose-300 bg-rose-50 px-1.5 py-0 text-rose-700 font-medium">
              Rare / Orphan only
            </span>
          )}
        </div>
      )}

      {/* Table */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Asset</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Company</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Therapeutic Area</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Indication</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Stage</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Status</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Emailing</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <FlaskConical className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No assets found</p>
                  </TableCell>
                </TableRow>
              ) : assets.map(a => (
                <TableRow
                  key={a.id}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/biotech/${a.company_id}`)}
                  data-testid={`asset-row-${a.id}`}
                >
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1.5">
                      <FlaskConical className="w-2.5 h-2.5 text-slate-400" />
                      <span className="text-[11px] font-medium text-slate-900">{a.asset_name}</span>
                      {a.is_orphan_rare && (
                        <span className="text-[8px] font-medium text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0 rounded cursor-help" title={a.rare_orphan_rationale ? `Why: ${a.rare_orphan_rationale}` : 'Tentative — matched "rare/orphan" wording in the asset text; not yet AI-verified'} data-testid={`asset-orphan-badge-${a.id}`}>Rare/Orphan</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-2.5 h-2.5 text-slate-400" />
                        <span className="text-[11px] text-slate-600">{a.company_name}</span>
                        <ExternalLink className="w-2 h-2 text-slate-300" />
                      </div>
                      {a.company_location && (
                        <span className="text-[9px] mt-0.5 ml-3.5 text-slate-400">
                          {a.company_location}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 text-[11px] text-slate-600">{a.therapeutic_area || '—'}</TableCell>
                  <TableCell className="py-2 text-[11px] text-slate-600">{a.indication || '—'}</TableCell>
                  <TableCell className="py-2">
                    {(() => {
                      const stageLabel = normalizeStageLabel(a.stage);
                      return (
                        <Badge variant="secondary" className={`text-[9px] px-1 py-0 ${STAGE_COLORS[stageLabel] || 'bg-slate-100 text-slate-600'}`} title={a.stage || ''}>
                          {stageLabel || '—'}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge variant="secondary" className={`text-[9px] px-1 py-0 ${STATUS_COLORS[a.status] || 'bg-slate-100 text-slate-600'}`}>
                      {a.status || 'Pipeline'}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge variant="secondary" className={`text-[9px] px-1 py-0 ${a.emailing_status === 'Completed' ? 'bg-green-100 text-green-700' : a.emailing_status === 'In Progress' ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>
                      {a.emailing_status || 'Not Started'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={currentPage} pages={totalPages} total={totalCount} onPageChange={handlePageChange} />
    </div>
  );
}
